import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  Clock3,
  Download,
  FileArchive,
  HardDriveDownload,
  Info,
} from "lucide-react"
import { useState } from "react"

import { DownloadStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { useDemoResetMaintenance } from "@/lib/demo-reset-status"
import { formatFileSize } from "@/lib/domain"
import { downloadsQueryOptions, serviceQueryKeys } from "@/lib/query-options"
import { prepareDownloadFn } from "@/server/functions"

export const Route = createFileRoute("/portal/downloads")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(downloadsQueryOptions()),
  head: () => ({
    meta: [{ title: "Downloads · Service Portal" }],
  }),
  component: DownloadsPage,
})

function DownloadsPage() {
  const resetInProgress = useDemoResetMaintenance()
  const queryClient = useQueryClient()
  const { data } = useSuspenseQuery(downloadsQueryOptions())
  const [downloadedId, setDownloadedId] = useState<string | null>(null)
  const prepareMutation = useMutation({
    mutationFn: (sceneId: string) => prepareDownloadFn({ data: { sceneId } }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serviceQueryKeys.downloads(),
        }),
        queryClient.invalidateQueries({
          queryKey: serviceQueryKeys.dashboard(),
        }),
      ])
    },
  })
  const filePercent =
    (data.allocation.activeFiles / data.allocation.fileLimit) * 100
  const sizePercent =
    (data.allocation.activeSizeGb / data.allocation.sizeLimitGb) * 100

  function downloadManifest(record: (typeof data.downloads)[number]) {
    const imageryFileName = record.fileName.replace(/\.zip$/i, ".tif")
    const metadataFileName = imageryFileName.replace(/\.tif$/i, ".xml")
    const manifest = {
      notice:
        "Synthetic Service Portal portfolio record. No satellite imagery is included.",
      downloadId: record.id,
      sceneId: record.sceneId,
      orderId: record.orderId,
      productFiles: [
        {
          role: "imagery",
          fileName: imageryFileName,
          mediaType: "image/tiff",
        },
        {
          role: "metadata",
          fileName: metadataFileName,
          mediaType: "application/xml",
        },
      ],
      demoSizeGb: record.sizeGb,
    }
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(manifest, null, 2)], {
        type: "application/json",
      })
    )
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = `${record.id}-manifest.json`
    anchor.click()
    URL.revokeObjectURL(url)
    setDownloadedId(record.id)
  }

  return (
    <div className="mx-auto max-w-[1400px]">
      <WorkspacePageHeader
        kicker="Allocation and release history"
        title="Download center"
        description="Monitor prepared packages, expiry, and the synthetic allocation attached to this demonstration account."
        actions={
          <Button render={<Link to="/portal/explorer" />}>
            Browse catalogue
            <ArrowRight />
          </Button>
        }
      />

      <section className="mt-6 grid overflow-hidden rounded-[14px] border border-border bg-white lg:grid-cols-[1fr_1fr_0.8fr]">
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-[var(--navy)]">
                Active file slots
              </p>
              <p className="mt-1 text-[0.68rem] text-muted-foreground">
                Prepared packages count against this limit
              </p>
            </div>
            <p className="text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
              {data.allocation.activeFiles}
              <span className="text-base text-muted-foreground">
                {" "}
                / {data.allocation.fileLimit}
              </span>
            </p>
          </div>
          <Progress
            value={filePercent}
            className="mt-5 h-2"
            aria-label="Active file slots used"
          />
        </div>
        <div className="border-t border-border p-5 sm:p-6 lg:border-t-0 lg:border-l">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-[var(--navy)]">
                Active package size
              </p>
              <p className="mt-1 text-[0.68rem] text-muted-foreground">
                Synthetic storage allowance
              </p>
            </div>
            <p className="text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
              {data.allocation.activeSizeGb.toFixed(1)}
              <span className="text-base text-muted-foreground">
                {" "}
                / {data.allocation.sizeLimitGb} GB
              </span>
            </p>
          </div>
          <Progress
            value={sizePercent}
            className="mt-5 h-2"
            aria-label="Active package storage used"
          />
        </div>
        <div className="border-t border-border bg-[#eaf7f8] p-5 sm:p-6 lg:border-t-0 lg:border-l">
          <p className="flex items-center gap-2 text-xs font-semibold text-[#164b59]">
            <Info className="size-4" />
            Demo download behavior
          </p>
          <p className="mt-2 text-[0.68rem] leading-5 text-[#42717b]">
            Available actions export a small manifest describing the expected
            GeoTIFF and XML pair. No imagery or production file is distributed.
          </p>
        </div>
      </section>

      <section className="mt-6 overflow-hidden rounded-[14px] border border-border bg-white">
        <div className="flex flex-col justify-between gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-semibold text-[var(--navy)]">
              Package history
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Prepared, expired, and pending synthetic packages
            </p>
          </div>
          <span className="text-xs font-medium text-muted-foreground">
            {data.downloads.length} records
          </span>
        </div>

        <div className="divide-y divide-border">
          {data.downloads.map((record) => (
            <article
              key={record.id}
              className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[52px_1fr_160px_150px_auto] lg:items-center"
            >
              <span className="grid size-11 place-items-center rounded-[10px] bg-[#e8f4f7] text-[var(--service-blue)]">
                <FileArchive className="size-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold text-[var(--navy)]">
                  {record.fileName.replace(/\.zip$/i, ".tif")}
                </p>
                <p className="mt-1 truncate font-mono text-[0.62rem] text-muted-foreground">
                  {record.fileName
                    .replace(/\.zip$/i, ".tif")
                    .replace(/\.tif$/i, ".xml")}{" "}
                  · metadata
                </p>
                {downloadedId === record.id ? (
                  <p
                    className="mt-1.5 text-[0.65rem] font-semibold text-[#246947]"
                    role="status"
                  >
                    Demo manifest downloaded
                  </p>
                ) : null}
              </div>
              <div className="text-xs">
                <p className="font-semibold text-[var(--ink)]">
                  {formatFileSize(record.sizeGb)}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {record.requestedLabel}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <DownloadStatusBadge status={record.status} />
                <span className="text-[0.62rem] text-muted-foreground">
                  {record.status === "available"
                    ? `until ${record.expiresLabel}`
                    : record.expiresLabel}
                </span>
              </div>
              <div className="flex justify-start lg:justify-end">
                {record.status === "available" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadManifest(record)}
                  >
                    <Download />
                    Demo manifest
                  </Button>
                ) : record.status === "expired" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => prepareMutation.mutate(record.sceneId)}
                    disabled={resetInProgress || prepareMutation.isPending}
                  >
                    <HardDriveDownload />
                    Prepare again
                  </Button>
                ) : (
                  <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock3 className="size-3.5" />
                    In queue
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
